import React from "react";
import { CheckCircle, Mail, Gift, ArrowRight, Shield, Facebook, Linkedin, Instagram } from "lucide-react";

export default function WaitlistSuccess() {
  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white flex flex-col">
      {/* NAV */}
      <div className="flex justify-between items-center px-6 py-4 border-b border-white/10">
        <img
          src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6824d581ae24f69d45e26536_beem%20logo%203.png"
          alt="Beem logo"
          className="w-24"
        />
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Shield size={14} /> Secure signup complete
        </div>
      </div>

      {/* MAIN */}
      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="max-w-xl w-full text-center">

          {/* SUCCESS ICON */}
          <div className="flex justify-center mb-6">
            <CheckCircle size={60} className="text-[#AF9B8A]" />
          </div>

          {/* HEADLINE */}
          <h1 className="text-4xl font-bold mb-4">
            You’re officially on the list!
          </h1>

          <p className="text-gray-300 text-lg mb-6">
            Nice one! Your early access is locked in and of course, that includes
            <span className="text-[#AF9B8A] font-semibold"> 3 months of Beem Plus for free</span>.
          </p>

          {/* HIGHLIGHT BOX */}
          <div className="bg-[#AF9B8A]/10 border border-[#AF9B8A]/30 rounded-xl p-6 mb-8 text-left">
            <div className="flex items-center gap-3 mb-3">
              <Gift size={18} className="text-[#AF9B8A]" />
              <p className="font-semibold text-[#AF9B8A]">
                Your early access perks
              </p>
            </div>

            <ul className="space-y-2 text-gray-300 text-sm">
              <li>• 3 months of Beem Plus completely free</li>
              <li>• Priority access before public launch</li>
              <li>• Early feature releases & updates</li>
              <li>• Direct input into product development</li>
            </ul>
          </div>

          {/* WHAT HAPPENS NEXT */}
          <div className="mb-10 text-left">
            <h3 className="text-xl font-semibold mb-3">
              What happens next?
            </h3>

            <div className="space-y-4 text-gray-300">
              <div className="flex items-start gap-3">
                <Mail size={16} className="mt-1" />
                <p>
                  We’ll send you an email as soon as Beem launches with your
                  <span className="text-[#AF9B8A]"> personal login code</span>.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <ArrowRight size={16} className="mt-1" />
                <p>
                  You’ll be able to log in, set up your properties and explore
                  everything before everyone else.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <ArrowRight size={16} className="mt-1" />
                <p>
                  Then sit back and enjoy a much simpler way to manage your
                  portfolio.
                </p>
              </div>
            </div>
          </div>

          {/* PRODUCT EXPLAINER */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-10 text-left">
            <h3 className="text-xl font-semibold mb-3">
              What is Beem?
            </h3>

            <p className="text-gray-300 text-sm mb-4">
              Beem is your all-in-one property management platform — built to
              replace spreadsheets, outdated software and juggling multiple
              tools.
            </p>

            <ul className="space-y-2 text-gray-300 text-sm">
              <li>• Track rent, payments and financials in real time</li>
              <li>• Manage tenants, documents and contracts</li>
              <li>• Automate repetitive admin (the boring stuff)</li>
              <li>• Get clear insights into your portfolio performance</li>
            </ul>

            <p className="text-gray-400 text-xs mt-4">
              Basically: less admin, more control, fewer headaches.
            </p>
          </div>

          {/* FAKE DASHBOARD PREVIEW */}
          <div className="mb-10">
            <h3 className="text-xl font-semibold mb-4">
              Here’s a sneak peek - don't show anyone else yet though ;)
            </h3>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 backdrop-blur">
              <img
                src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/692084beeaa10b20fc37aa83_Beem%20analytics.jpg"
                alt="Beem dashboard preview"
                className="rounded-lg w-full"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Clean, simple and built for people who don’t enjoy spreadsheets.
            </p>
          </div>

          {/* SOCIALS */}
          <div className="mb-10">
            <h3 className="text-lg font-semibold mb-3">
              Connect with us
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Follow along for updates, sneak peeksband the occasional useful insight.
            </p>
            <div className="flex justify-center gap-6">
              <a href="https://www.facebook.com/beem.tech" target="_blank" rel="noopener noreferrer">
                <Facebook className="hover:text-white transition" />
              </a>
              <a href="https://www.linkedin.com/company/beem-tech/" target="_blank" rel="noopener noreferrer">
                <Linkedin className="hover:text-white transition" />
              </a>
              <a href="https://www.instagram.com/beem.network/" target="_blank" rel="noopener noreferrer">
                <Instagram className="hover:text-white transition" />
              </a>
            </div>
          </div>

          {/* FOOTNOTE */}
          <p className="text-xs text-gray-500">
            Keep an eye on your inbox (and maybe spam, just in case). We’ll make
            it worth the wait.
          </p>
        </div>
      </div>

      {/* FOOTER */}
      <div className="text-center text-xs text-gray-500 py-6 border-t border-white/10">
        © {new Date().getFullYear()} Beem • Built for modern landlords
      </div>
    </div>
  );
}
